import { ArrowRight, Phone, Check, Quote, Star, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import ConceptHeader from "./ConceptHeader";

/**
 * "Service Pro" layout — bold, urgent, conversion-focused.
 * Used by trade-service concepts (roofing, HVAC, plumbing).
 * Distinct features: hero photo split + inline lead form, big trust stats
 * band, before/after split, urgency final CTA.
 */
export default function LayoutServicePro({ c }) {
  const t = c.theme;
  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.fontBody, minHeight: "100vh" }}>
      <ConceptHeader industry={c.industry} />

      {/* HERO with photo */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: t.heroBg }}/>
        <div
          className="absolute inset-0 opacity-50 mix-blend-luminosity"
          style={{ background: `url(${c.images.hero}) center/cover no-repeat` }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(95deg, ${t.bg}E6 0%, ${t.bg}AA 50%, transparent 100%)` }}/>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-20 md:pb-28">
          {/* Faux site nav */}
          <div className="flex items-center justify-between mb-16">
            <p style={{ fontFamily: t.fontDisplay, fontSize: 24, color: t.text }}>{c.name}</p>
            <nav className="hidden md:flex items-center gap-7 text-[13px]" style={{ color: t.muted }}>
              <span>Services</span><span>About</span><span>Reviews</span><span>Contact</span>
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px]"
                    style={{ background: t.accent, color: "#fff" }}>
                <Phone size={13}/> Call now
              </span>
            </nav>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>{c.hero.eyebrow}</p>
              <h1 className="mt-6 leading-[1.04]"
                  style={{ fontFamily: t.fontDisplay, fontSize: "clamp(40px, 6vw, 80px)", fontWeight: 500 }}>
                {c.hero.title}
              </h1>
              <p className="mt-7 text-lg md:text-xl leading-relaxed max-w-xl" style={{ color: t.muted }}>
                {c.hero.sub}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-[15px]"
                      style={{ background: t.accent, color: "#fff" }}>
                  {c.hero.ctaPrimary} <ArrowRight size={16}/>
                </span>
                <span className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-medium text-[15px] border"
                      style={{ borderColor: t.muted, color: t.text }}>
                  {c.hero.ctaSecondary.startsWith("Call") && <Phone size={15}/>}
                  {c.hero.ctaSecondary}
                </span>
              </div>
            </div>

            {/* Quote form */}
            <div className="lg:col-span-5">
              <div className="p-8 rounded-2xl border backdrop-blur" style={{ background: t.surface + "F0", borderColor: t.muted + "33" }}>
                <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Free quote</p>
                <p style={{ fontFamily: t.fontDisplay, fontSize: 26, marginTop: 6 }}>Tell us about the job.</p>
                <div className="mt-6 space-y-3">
                  {["Full name", "Phone or email", "What's the issue or project?"].map((p, i) => (
                    <div key={i} className="px-4 py-3 rounded-md text-[13px]" style={{ background: t.bg, color: t.muted, border: `1px solid ${t.muted}33` }}>
                      {p}
                    </div>
                  ))}
                </div>
                <div className="mt-5 w-full py-3 rounded-full text-center font-medium text-[14px]"
                     style={{ background: t.accent, color: "#fff" }}>
                  Get my quote
                </div>
                <p className="mt-3 text-[11px] text-center" style={{ color: t.muted }}>
                  Replies within 1 business hour, 7 days a week.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STATS BAND */}
      <section className="py-12 border-y" style={{ background: t.surface, borderColor: t.muted + "22" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {c.stats.map((s) => (
            <div key={s.v} className="text-center md:text-left">
              <p style={{ fontFamily: t.fontDisplay, fontSize: 36, color: t.accent }}>{s.k}</p>
              <p className="mt-1 text-[12px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES — cards with photo accent */}
      <section className="py-24 md:py-28" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>What we do</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 500 }}>
              Full-service. Done right.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.services.map((s, i) => (
              <div key={s.title} className="p-7 rounded-2xl border transition-colors"
                   style={{ background: t.surface, borderColor: t.muted + "22" }}>
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full font-mono text-[13px]"
                      style={{ background: t.accent + "20", color: t.accent }}>0{i+1}</span>
                <h3 className="mt-5 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: 22 }}>{s.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORK PROOF — split image / why */}
      <section className="relative" style={{ background: t.surface }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative h-[420px] rounded-2xl overflow-hidden"
               style={{ background: `url(${c.images.secondary}) center/cover` }}>
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${t.bg}CC 100%)` }}/>
            <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 text-[13px]" style={{ color: t.text }}>
              <Shield size={16} style={{ color: t.accent }}/>
              <span>Licensed, bonded, and insured in every state we serve.</span>
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Why {c.name.split(" ")[0]}</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(28px, 3.5vw, 44px)", fontWeight: 500 }}>
              Built on a reputation we don't take for granted.
            </h2>
            <ul className="mt-8 space-y-3">
              {c.valueProps.map((v) => (
                <li key={v} className="flex items-start gap-3 text-[16px]">
                  <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0"
                        style={{ background: t.accent, color: "#fff" }}>
                    <Check size={13}/>
                  </span>
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24" style={{ background: t.bg }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex justify-center gap-1 mb-6">
            {Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={18} fill={t.accent} stroke={t.accent}/>))}
          </div>
          <Quote size={32} className="mx-auto" style={{ color: t.accent }}/>
          <p className="mt-6 leading-tight"
             style={{ fontFamily: t.fontDisplay, fontSize: "clamp(26px, 3.4vw, 40px)", fontWeight: 500 }}>
            "{c.testimonial.quote}"
          </p>
          <p className="mt-8 text-[14px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>— {c.testimonial.author}</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-24" style={{ background: t.surface }}>
        <div className="absolute -bottom-20 -right-20 w-[360px] h-[360px] rounded-full opacity-25"
             style={{ background: t.accent }}/>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Ready when you are</p>
          <h2 className="mt-4 leading-tight"
              style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 500 }}>
            {c.finalCta}.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-[15px]"
                  style={{ background: t.accent, color: "#fff" }}>
              {c.hero.ctaPrimary} <ArrowRight size={16}/>
            </span>
            <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-[15px] border"
                  style={{ borderColor: t.muted, color: t.text }}>
              {c.hero.ctaSecondary}
            </span>
          </div>
        </div>
      </section>

      <ConceptFooter accent={t.accent} muted={t.muted} bg={t.bg}/>
    </div>
  );
}

export function ConceptFooter({ accent, muted, bg }) {
  return (
    <footer className="py-10 border-t" style={{ borderColor: muted + "22", background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[13px]" style={{ color: muted }}>
        <p>This is a fictional concept by <Link to="/" className="underline" style={{ color: accent }}>Shumway Tech</Link>. Want one for your business?</p>
        <Link to="/#contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium" style={{ background: accent, color: "#fff" }}>
          Request a free concept <ArrowRight size={14}/>
        </Link>
      </div>
    </footer>
  );
}
